import React, { useEffect, useState, useRef } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { CheckCircle2Icon, XIcon, MapPinIcon } from 'lucide-react';
const NAMES = ['Sofía', 'María', 'Camila', 'Florencia', 'Agustina', 'Martina', 'Valentina', 'Lucía', 'Paula', 'Daniela', 'Belén', 'Carla', 'Julieta', 'Romina', 'Antonella', 'Mariana', 'Micaela', 'Brenda', 'Rocío', 'Natalia', 'Soledad', 'Verónica', 'Carolina', 'Lorena', 'Gabriela', 'Andrea', 'Patricia', 'Fernanda', 'Cintia', 'Eliana', 'Pilar', 'Ailén', 'Tamara', 'Macarena'];
const SURNAMES_INITIAL = ['G.', 'P.', 'M.', 'R.', 'L.', 'S.', 'F.', 'C.', 'B.', 'A.', 'D.', 'V.', 'T.', 'N.'];
const LOCATIONS = ['Buenos Aires', 'La Plata', 'Rosario', 'Córdoba', 'Mendoza', 'Tucumán', 'Mar del Plata', 'Salta', 'Santa Fe', 'San Juan', 'Neuquén', 'Bahía Blanca', 'Resistencia', 'Posadas', 'Corrientes', 'San Luis', 'Río Cuarto', 'Paraná', 'Comodoro Rivadavia', 'La Rioja', 'Catamarca', 'Bariloche', 'CABA', 'San Salvador de Jujuy', 'Tandil', 'Olavarría', 'Pergamino', 'Villa María', 'Concordia', 'Quilmes', 'Lanús', 'Avellaneda', 'San Isidro', 'Tigre', 'Morón', 'Ramos Mejía'];
const PLANS = ['Kit Esencial', 'Edición Completa'];
const PLAN_WEIGHTS = [0.35, 0.65]; // Edición Completa más probable
function randomItem<T>(arr: T[]): T {
  return arr[Math.floor(Math.random() * arr.length)];
}
function randomPlan() {
  const r = Math.random();
  return r < PLAN_WEIGHTS[0] ? PLANS[0] : PLANS[1];
}
interface Purchase {
  id: number;
  name: string;
  location: string;
  plan: string;
  minutesAgo: number;
}
function generatePurchase(): Purchase {
  return {
    id: Date.now() + Math.random(),
    name: `${randomItem(NAMES)} ${randomItem(SURNAMES_INITIAL)}`,
    location: randomItem(LOCATIONS),
    plan: randomPlan(),
    minutesAgo: Math.floor(Math.random() * 8) + 1
  };
}
export function SocialProofToast() {
  const [current, setCurrent] = useState<Purchase | null>(null);
  const [dismissed, setDismissed] = useState(false);
  const timeoutsRef = useRef<number[]>([]);
  useEffect(() => {
    if (dismissed) return;
    const VISIBLE_MS = 6000;
    const MIN_GAP_MS = 40000;
    const MAX_GAP_MS = 60000;
    const INITIAL_DELAY_MS = 10000;
    const clearAll = () => {
      timeoutsRef.current.forEach(t => clearTimeout(t));
      timeoutsRef.current = [];
    };
    const showNext = () => {
      setCurrent(generatePurchase());
      const hideTimer = window.setTimeout(() => {
        setCurrent(null);
        const gap = MIN_GAP_MS + Math.random() * (MAX_GAP_MS - MIN_GAP_MS);
        const nextTimer = window.setTimeout(showNext, gap);
        timeoutsRef.current.push(nextTimer);
      }, VISIBLE_MS);
      timeoutsRef.current.push(hideTimer);
    };
    const initialTimer = window.setTimeout(showNext, INITIAL_DELAY_MS);
    timeoutsRef.current.push(initialTimer);
    return clearAll;
  }, [dismissed]);
  const handleClose = () => {
    setCurrent(null);
    setDismissed(true);
  };
  return <div aria-live="polite" className="fixed left-3 md:left-6 z-40 pointer-events-none bottom-24 md:bottom-6" data-id="element-225">
      <AnimatePresence mode="wait" data-id="element-226">
        {current && <motion.div key={current.id} initial={{
        opacity: 0,
        y: 20,
        scale: 0.95
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={{
        opacity: 0,
        y: 10,
        scale: 0.95
      }} transition={{
        duration: 0.35,
        ease: 'easeOut'
      }} className="pointer-events-auto bg-white shadow-2xl border border-slate-200 rounded-2xl p-3 pr-9 max-w-[280px] md:max-w-xs relative" role="status" data-id="element-227">
            {/* Close */}
            <button type="button" onClick={handleClose} aria-label="Cerrar notificación" className="absolute top-2 right-2 w-6 h-6 rounded-full flex items-center justify-center text-slate-400 hover:bg-slate-100 hover:text-slate-700 transition-colors" data-id="element-228">
              <XIcon size={14} data-id="element-229" />
            </button>

            <div className="flex items-start gap-2.5" data-id="element-230">
              {/* Avatar + verified */}
              <div className="shrink-0 relative" data-id="element-231">
                <div className="w-9 h-9 md:w-10 md:h-10 rounded-full bg-teal-100 text-teal-900 flex items-center justify-center font-serif font-bold text-sm md:text-base" data-id="element-232">
                  {current.name.charAt(0)}
                </div>
                <div className="absolute -bottom-0.5 -right-0.5 bg-white rounded-full" data-id="element-233">
                  <CheckCircle2Icon size={14} className="text-green-600" fill="white" data-id="element-234" />
                </div>
              </div>

              {/* Content */}
              <div className="min-w-0 flex-grow pt-0.5" data-id="element-235">
                <div className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-green-700 mb-0.5" data-id="element-236">
                  <CheckCircle2Icon size={11} data-id="element-237" />
                  <span data-id="element-238">Compra verificada</span>
                </div>
                <p className="text-[13px] md:text-sm font-bold text-teal-900 leading-tight" data-id="element-239">
                  {current.name}
                </p>
                <p className="text-[11px] md:text-xs text-slate-600 leading-tight mt-0.5 flex items-center gap-0.5" data-id="element-240">
                  <MapPinIcon size={10} className="text-slate-400" data-id="element-241" />
                  <span data-id="element-242">{current.location}, Argentina</span>
                </p>
                <p className="text-[11px] md:text-xs text-slate-500 mt-1 leading-tight" data-id="element-243">
                  Compró el{' '}
                  <span className="font-bold text-terracotta" data-id="element-244">
                    {current.plan}
                  </span>
                </p>
                <p className="text-[10px] text-slate-400 mt-0.5" data-id="element-245">
                  hace {current.minutesAgo} min
                </p>
              </div>
            </div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}